import TitleDescription from '@/common/components/base/typography/TitleDescription';
import UploadDocumentCameraPage from '@/common/components/camera/pages/UploadDocumentCameraPage';
import EditImage from '@/common/components/imageGallery/components/editImage/EditImage';
import { ImageType } from '@/common/components/imageGallery/components/Extensions/moreItemsExtension/moreItemsExtension.type';
import MainLayout from '@/common/components/layouts/MainLayout';
import UploadDocumentImageConfirmation from '@/common/components/uploadImage/UploadDocumentImageConfirmation';
import UploadImageCard from '@/common/components/uploadImage/UploadImageCard';
import useModal from '@/common/hooks/useModal';
import LoadingPage from '@/common/pages/LoadingPage';
import { FileRoleType } from '@/common/types/ApiResponseTypes.type';
import userTypeTitleConvertor from '@/common/utils/userTypeTitleConvertor';
import { deleteDamagedAdjusterDocumentImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/deleteDamagedAdjusterDocumentImages/deleteDamagedAdjusterDocumentImagesQueryOptions';
import { GetDamagedAdjusterDocumentImagesQueryResponseType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedAdjusterDocumentImages/getDamagedAdjusterDocumentImagesQuery.type';
import { getDamagedAdjusterDocumentImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedAdjusterDocumentImages/getDamagedAdjusterDocumentImagesQueryOptions';
import { postDamagedAdjusterDocumentImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/postDamagedAdjusterDocumentImages/postDamagedAdjusterDocumentImagesQueryOptions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';

export default ({ userType }: { userType: FileRoleType }) => {
  const title = userTypeTitleConvertor(userType);
  return (
    <MainLayout
      title={`${title} / تصاویر`}
      header="titleHeader"
      hasBottomPadding
    >
      <AdjusterDocumentsImages userType={userType} />
    </MainLayout>
  );
};

const AdjusterDocumentsImages = ({ userType }: { userType: FileRoleType }) => {
  const [uploadLoadingId, setUploadLoadingId] = useState<string | null>(null);
  const { id, damagedId } = useParams();
  const queryClient = useQueryClient();
  const {
    closeModal: previewBeforeUploadClose,
    open: previewBeforeUploadOpen,
    openModal: previewBeforeUploadOpenHandler,
  } = useModal();
  const {
    closeModal: editUploadedImageClose,
    open: editUploadedImageOpen,
    openModal: editUploadedImageOpenHandler,
  } = useModal();

  const {
    closeModal: retakeUploadedImageClose,
    open: retakeUploadedImageOpen,
    openModal: retakeUploadedImageOpenHandler,
  } = useModal();

  const [uploadImageData, setUploadImageData] = useState<
    | {
        image: GetDamagedAdjusterDocumentImagesQueryResponseType;
        imageData: ImageType;
      }
    | undefined
  >(undefined);

  const { mutateAsync: uploadImage, isPending: uploadImageLoading } =
    useMutation(postDamagedAdjusterDocumentImagesQueryOptions());
  const { mutateAsync: deleteImage, isPending } = useMutation(
    deleteDamagedAdjusterDocumentImagesQueryOptions(),
  );
  const { data: documentImagesList, isRefetching: getListLoading } = useQuery(
    getDamagedAdjusterDocumentImagesQueryOptions({
      damagedId: Number(damagedId),
      type: userType,
      id: Number(id),
    }),
  );

  const invalidateGetList = () => {
    queryClient
      .invalidateQueries({
        queryKey: getDamagedAdjusterDocumentImagesQueryOptions({
          damagedId: Number(damagedId),
          type: userType,
          id: Number(id),
        }).queryKey,
      })
      .then(() => {
        setUploadLoadingId(null);
      });
  };
  const uploadImageHandler = ({
    imageItem,
    CapturedImageData,
    onSuccess,
  }: {
    imageItem: GetDamagedAdjusterDocumentImagesQueryResponseType;
    CapturedImageData: ImageType;
    onSuccess?: () => void;
  }) => {
    setUploadLoadingId(imageItem.title.const);

    uploadImage({
      type: userType,
      id: Number(id),
      damagedId: Number(damagedId),
      data: { image: CapturedImageData.blob, title: imageItem.title.const },
    }).then(() => {
      invalidateGetList();
      onSuccess?.();
    });
  };

  return (
    <Box pt="1.5rem">
      <TitleDescription>تصاویر مدارک</TitleDescription>
      <Flex
        gap="1rem"
        maxWidth="maxContainer"
        margin="auto"
        mt="1.5rem"
        className={grid({
          columns: getListLoading && !uploadLoadingId ? 1 : 2,
        })}
      >
        {getListLoading && !uploadLoadingId ? (
          <LoadingPage />
        ) : (
          documentImagesList?.map((image) => {
            return (
              <UploadImageCard
                title={image.title.fa_const}
                deleteImageLoading={isPending}
                key={image.title.const}
                imageData={image}
                selectFromGallery
                onUploadImage={({ imageData, closeModal }) => {
                  previewBeforeUploadOpenHandler();
                  closeModal();
                  setUploadImageData({ image, imageData });
                }}
                onEditImage={({ closeModal, imageData }) =>
                  uploadImageHandler({
                    CapturedImageData: imageData,
                    imageItem: image,
                    onSuccess: closeModal,
                  })
                }
                onDeleteImage={({ closeModal }) => {
                  deleteImage({
                    type: userType,
                    id: Number(id),
                    damagedId: Number(damagedId),
                    data: { image_id: image.id?.toString()! },
                  }).then(() => {
                    closeModal();
                    invalidateGetList();
                  });
                }}
                uploadImageLoading={
                  image.title.const === uploadLoadingId &&
                  (uploadImageLoading || getListLoading)
                }
              />
            );
          })
        )}
      </Flex>
      {previewBeforeUploadOpen ? (
        <UploadDocumentImageConfirmation
          confirmationHandler={() => {
            uploadImageHandler({
              CapturedImageData: uploadImageData?.imageData!,
              imageItem: uploadImageData?.image!,
            });
          }}
          editHandler={editUploadedImageOpenHandler}
          image={uploadImageData?.imageData!}
          onClose={previewBeforeUploadClose}
          retakeHandler={retakeUploadedImageOpenHandler}
        />
      ) : null}
      {editUploadedImageOpen ? (
        <EditImage
          image={uploadImageData?.imageData.url!}
          onClose={editUploadedImageClose}
          onSave={(data) => {
            setUploadImageData((prev) => ({
              image: prev?.image!,
              imageData: data,
            }));

            editUploadedImageClose();
            previewBeforeUploadOpenHandler();
          }}
        />
      ) : null}

      {retakeUploadedImageOpen && (
        <UploadDocumentCameraPage
          onDenyCamera={() => {}}
          updateImage={(data) => {
            setUploadImageData((prev) => ({
              image: prev?.image!,
              imageData: data,
            }));
            retakeUploadedImageClose();
            previewBeforeUploadOpenHandler();
          }}
          closeCamera={retakeUploadedImageClose}
        />
      )}
    </Box>
  );
};
